import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'
import { operations as loadingOperations } from 'store/loadingManager'

const INITIAL_LOADING_TEXT = 'Loading digital licenses...'

/**
 * Hook to fetch digital licenses
 * @returns {Object} Object containing licenses and fetching status
 */
export const useDigitalLicenses = () => {
  const dispatch = useDispatch()

  // Select necessary state from Redux
  const activeDigitalLicenses = useSelector(
    licenseSelectors.selectActiveDigitalLicenses,
  )
  const isFetchingActiveDigitalLicenses = useSelector(
    state => state.licenseManager?.isFetchingActiveDigitalLicenses,
  )

  // Function to fetch digital licenses
  const fetchActiveDigitalLicensesForUser = () => {
    dispatch(licenseOperations.fetchActiveDigitalLicensesForUser())
  }

  // Fetch licenses if needed
  useEffect(() => {
    if (
      !Array.isArray(activeDigitalLicenses) &&
      !isFetchingActiveDigitalLicenses
    ) {
      fetchActiveDigitalLicensesForUser()
    }
  }, [activeDigitalLicenses, isFetchingActiveDigitalLicenses])

  return {
    activeDigitalLicenses,
    isFetchingActiveDigitalLicenses,
    fetchActiveDigitalLicensesForUser,
  }
}

/**
 * Hook to manage loading states
 * @param {boolean} isLoading - Whether some operation is loading
 * @param {string} loadingText - Text to display during loading
 * @returns {Object} Object containing loading state management functions
 */
export const useDigitalLicenseLoadingState = (
  loadingText = INITIAL_LOADING_TEXT,
) => {
  const dispatch = useDispatch()
  const isLoadingApp = useSelector(state => state.loadingManager?.isLoading)
  const isLoadingDigitalLicenses = useSelector(
    licenseSelectors.selectActiveDigitalLicenses,
  )

  const setAppIsLoadingState = text => {
    dispatch(loadingOperations.setIsLoadingState(text))
  }

  const setAppNotLoadingState = () => {
    dispatch(loadingOperations.setNotLoadingState())
  }

  // Update loading state based on isLoading parameter
  useEffect(() => {
    if (isLoadingDigitalLicenses && !isLoadingApp) {
      setAppIsLoadingState(loadingText)
    }
    if (!isLoadingDigitalLicenses && isLoadingApp) {
      setAppNotLoadingState()
    }
  }, [isLoadingDigitalLicenses, isLoadingApp, loadingText])

  // Clean up loading state on unmount
  useEffect(() => {
    return () => {
      if (isLoadingApp) {
        setAppNotLoadingState()
      }
    }
  }, [isLoadingApp])

  return {
    isLoadingApp,
    setAppIsLoadingState,
    setAppNotLoadingState,
  }
}

/**
 * Combined hook that handles both license fetching and loading state
 * @returns {Object} Combined object with license data and loading state
 */
export const useDigitalLicensesWithLoading = () => {
  const licenseData = useDigitalLicenses()
  const loadingState = useDigitalLicenseLoadingState()

  return {
    ...licenseData,
    ...loadingState,
  }
}
